/**
 * MF-CUSTOM (Empat): prev/next arrows for the cart upsell slider (sections/cart-drawer.liquid).
 *
 * The theme's own <carousel-prev-button>/<carousel-next-button> step through a cell index on
 * <scroll-carousel>. With 2.5 cards visible, the last reachable scroll position is reached before the
 * last index, so "next" kept raising the index without moving, and "previous" then aimed at positions
 * the list couldn't scroll to, which made it look broken. These arrows move to the next/previous card
 * from wherever the list actually is, and enable/disable from the real scroll position. The list is a plain scroll container, not <scroll-carousel>: that element also jumped back
 * to its remembered cell on every resize (e.g. lazy images loading mid-scroll), swallowing clicks. The
 * cart drawer re-renders its content on every cart change; the element re-initialises itself each time
 * it's connected (script is loaded from layout/theme.liquid, see snippets/mf-cart-upsell.liquid).
 * See CUSTOM-CHANGES.md.
 */
class MfCartUpsell extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.mf-cart-upsell__list');
    this.prevButton = this.querySelector('[data-direction="prev"]');
    this.nextButton = this.querySelector('[data-direction="next"]');
    if (!this.list || !this.prevButton || !this.nextButton) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.prevButton.addEventListener('click', () => this.scrollByCard(-1), { signal });
    this.nextButton.addEventListener('click', () => this.scrollByCard(1), { signal });
    this.list.addEventListener('scroll', () => this.scheduleUpdate(), { signal, passive: true });

    this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
    this.resizeObserver.observe(this.list);
    this.updateButtons();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
  }

  // scrollLeft is negative in RTL, so flip the direction there
  get directionSign() {
    return getComputedStyle(this.list).direction === 'rtl' ? -1 : 1;
  }

  // Scroll position at which each card is aligned to the start, clamped to the end. Measured from the
  // first card's box so it's independent of the current scroll and of the list's padding.
  get cardPositions() {
    const cards = Array.from(this.list.children);
    if (!cards.length) return [];

    const firstRect = cards[0].getBoundingClientRect();
    const maxPosition = this.list.scrollWidth - this.list.clientWidth;
    const isRtl = this.directionSign === -1;

    return cards.map((card) => {
      const rect = card.getBoundingClientRect();
      return Math.min(maxPosition, Math.round(isRtl ? firstRect.right - rect.right : rect.left - firstRect.left));
    });
  }

  // Jump to the next/previous card's exact snap position rather than scrolling by a relative amount:
  // from the end position (which isn't card-aligned) a relative scroll got snapped straight back, since
  // the stock cards use scroll-snap-stop: always.
  scrollByCard(step) {
    const positions = this.cardPositions;
    if (!positions.length) return;

    const current = Math.abs(this.list.scrollLeft);
    const target = step > 0
      ? positions.find((position) => position > current + 2) ?? positions[positions.length - 1]
      : [...positions].reverse().find((position) => position < current - 2) ?? 0;

    this.list.scrollTo({ left: target * this.directionSign, behavior: 'smooth' });
  }

  scheduleUpdate() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.updateButtons());
  }

  updateButtons() {
    const position = Math.abs(this.list.scrollLeft);
    const maxPosition = this.list.scrollWidth - this.list.clientWidth;

    this.prevButton.disabled = position <= 1;
    this.nextButton.disabled = position >= maxPosition - 1;
    // everything fits (e.g. 4 products on the wide cart page): hide the arrows instead of showing two disabled ones
    this.toggleAttribute('data-static', maxPosition <= 1 && this.list.clientWidth > 0);
  }
}

if (!customElements.get('mf-cart-upsell')) {
  customElements.define('mf-cart-upsell', MfCartUpsell);
}
